import { onScopeDispose } from 'vue';
import { useDebounceFn, useEventListener } from '@vueuse/core';
import { useNuxtApp } from '#app';
import { Cookie } from '@nuxt-common/util/app/utils/cookie';
import getVisitorId from '#shared/utils/visitorId';
import { useEnvProps } from '~/stores/envProps';

let previousUserId = '';
let previousVisitorId = '';
let areTriggersInitialized = false;

export const useAskAIRefresh = () => {
  const { isMcom } = useEnvProps();
  const nuxtApp = useNuxtApp();
  const userIdCookieKey = `${isMcom ? 'macys' : 'bloomingdales'}_online_uid`;

  const refreshAskAIToken = useDebounceFn(async () => {
    if (window.googleAgent?.refreshToken) {
      try {
        await window.googleAgent.refreshToken();
      } catch (error) {
        console.error('Error refreshing Ask AI token', error);
      }
    }
  }, 500);

  const refreshIfMetadataChanged = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    const currentUserId = Cookie.get(userIdCookieKey) || '';
    const currentVisitorId = getVisitorId();
    if (currentUserId !== previousUserId || currentVisitorId !== previousVisitorId) {
      updatePreviousMetadata(currentUserId, currentVisitorId);
      refreshAskAIToken();
    }
  };

  const initAskAIRefreshTriggers = () => {
    if (areTriggersInitialized) {
      return;
    }
    areTriggersInitialized = true;

    // Initialize previous IDs only once using nullish coalescing
    previousUserId = sessionStorage.getItem('previousUserId') ?? Cookie.get(userIdCookieKey) ?? '';
    previousVisitorId = sessionStorage.getItem('previousVisitorId') ?? getVisitorId();

    // Event listeners are automatically cleanup by VueUse useEventListener!
    if (!previousUserId) {
      useEventListener(window, 'updateQuickBagCount', refreshIfMetadataChanged, { once: true });
    }
    useEventListener(window, 'focus', refreshIfMetadataChanged);
    useEventListener(document, 'visibilitychange', refreshIfMetadataChanged);
    useEventListener(window, 'storeChangedOnPage', refreshAskAIToken);
    useEventListener(window, 'storeChangedOnHeader', refreshAskAIToken);

    const unhookLoaded = nuxtApp.hook('ask-ai:google-agent-loaded', refreshIfMetadataChanged);

    // If Google Agent is already loaded (e.g. instant load from browser cache),
    // immediately check for metadata changes.
    if (window.googleAgent) {
      refreshIfMetadataChanged();
    }

    onScopeDispose(() => {
      if (typeof unhookLoaded === 'function') {
        unhookLoaded();
      }
      areTriggersInitialized = false;
    });
  };

  const updatePreviousMetadata = (userId: string, visitorId: string) => {
    previousUserId = userId;
    previousVisitorId = visitorId;
    sessionStorage.setItem('previousUserId', userId);
    sessionStorage.setItem('previousVisitorId', visitorId);
  };

  return {
    initAskAIRefreshTriggers,
    updatePreviousMetadata,
  };
};
